import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { INVESTOR_MESSAGES, ISSUER_MESSAGES } from 'appRootPath/messages';
import {
  AuctionObject, BidThread,
  BidAction, BidNegotiation
} from 'appRootPath/models/commercial-papers.model';
import { AuctionService } from 'appRootPath/services/auction.service';
import { CpAuctionApiService } from 'appRootPath/services/cp-auction-api.service';
import { SharedDataService } from 'appRootPath/services/shared-data.service';
import { ToastrService } from 'ngx-toastr';
import { BID_ACTIONS } from '../auction-metadata';
import { AuctionPrivacyEnum, CpRolesEnum, UserData } from '../global-meta-data';

@Component({
  selector: 'app-bid-sidepanel',
  templateUrl: './bid-sidepanel.component.html',
  styleUrls: ['./bid-sidepanel.component.scss']
})
export class BidSidepanelComponent implements OnInit {
  @Input() auctionDetails: AuctionObject;
  @Input() isInvestor = false;
  @Input() bidThread: BidThread;
  @Input() bidThreadId;
  @Input() action: string;

  @Output() sidebarCloseEvent = new EventEmitter<any>();

  userData: UserData = this.sharedDataService.getUserDataFromLS();
  stateNextActionsMap;
  actionConfigMap;
  currentStateConfigMap;
  PLACE_A_BID_ACTION = BID_ACTIONS.PLACE_BID;
  otherInvestorBids: BidNegotiation[];

  constructor(
    private sharedDataService: SharedDataService,
    private auctionApi: CpAuctionApiService,
    private auctionService: AuctionService,
    private toastr: ToastrService
  ) {
    this.initBiddingConfig();
  }

  /**
 * Initialize the component's bidding config properties
 */
  initBiddingConfig() {
    this.stateNextActionsMap = this.auctionService.roleBiddingConfig.stateNextActionsMap;
    this.actionConfigMap = this.auctionService.roleBiddingConfig.actionConfigMap;
    this.currentStateConfigMap = this.auctionService.roleBiddingConfig.currentStateConfigMap;
  }

  ngOnInit(): void {
    this.otherInvestorBids = [];
    if (this.auctionDetails && this.auctionDetails.bid_thread) {
      this.auctionDetails.bid_thread.forEach((bidThread: any) => {
        if (bidThread.bid) {
          const investorBids = bidThread.bid.filter(bid => bid.created_by_role_type === CpRolesEnum.INVESTOR);
          this.otherInvestorBids.push(investorBids[investorBids.length - 1]);
        }
      });
    }
  }

  closeSidePanel(eventData = null) {
    if (eventData) {
      this.auctionService.refreshAuctionSubject.next(this.auctionDetails.id);
    }
    this.sidebarCloseEvent.emit(eventData);
  }

  /**
   * Handle accept and reject actions here.
   */
  handleBidAction(bidId: string, bidAction: string): void {
    if ([BID_ACTIONS.ACCEPT_BID, BID_ACTIONS.ACCEPT_NEGOTIATION,
    BID_ACTIONS.ACCEPT_REEVAL_CBC, BID_ACTIONS.ACCEPT_BID_WITH_CBC].includes(bidAction)) {
      this.handleAcceptOrReject(bidId, true, bidAction);
    } else if ([BID_ACTIONS.REJECT_BID, BID_ACTIONS.REJECT_NEGOTIATION,
    BID_ACTIONS.REJECT_REEVAL_CBC].includes(bidAction)) {
      this.handleAcceptOrReject(bidId, false, bidAction);
    }
  }

  handleAcceptOrReject(bidId: string, isAccept: boolean, bidAction: string): void {
    this.auctionApi.acceptOrRejectBid(bidId, isAccept, bidAction).subscribe((bidResp: BidNegotiation) => {
      if (bidResp.id) {
        this.toastr.success(this.getAcceptOrRejectToastrMsg(this.isInvestor, isAccept));
        this.closeSidePanel(bidResp);
      }
    });
  }

  getAcceptOrRejectToastrMsg(isInvestor: boolean, isAccept: boolean): string {
    let toastMessage: string;

    if (isAccept) {
      toastMessage = isInvestor ? INVESTOR_MESSAGES.ACCEPT_BID_SUCCESS : ISSUER_MESSAGES.ACCEPT_OFFER_SUCCESS;
    } else {
      toastMessage = isInvestor ? INVESTOR_MESSAGES.REJECT_BID_SUCCESS : ISSUER_MESSAGES.REJECT_OFFER_SUCCESS;
    }

    return toastMessage;
  }


  onBidFormSubmit(bidFormData: BidNegotiation) {
    const bidRequest: BidNegotiation = Object.assign({}, bidFormData, {
      thread: this.bidThreadId,
      bidder: this.userData.auth_details.id,
      state_value: this.action,
      is_active: true
    });

    if (this.action === BID_ACTIONS.PLACE_BID) {
      if (this.auctionDetails.auction_privacy_type === AuctionPrivacyEnum.OPEN) {
        this.createThreadAndBid(bidRequest);
      }
      if (this.auctionDetails.auction_privacy_type === AuctionPrivacyEnum.SECRET) {
        const matchingThread = this.auctionDetails.bid_thread.
          find(bidThread => bidThread.investor_role === this.sharedDataService.getUserDataFromLS().roleId);
        bidRequest.thread = matchingThread && matchingThread.id;
        this.placeBidOnExistingThread(bidRequest);
      }
    } else {
      this.placeBidOnExistingThread(bidRequest);
    }
  }

  createThreadAndBid(bidRequest) {
    const bidThreadRequest: BidThread = Object.assign({}, {
      auction: this.auctionDetails.id,
      investor_role: this.userData.roleId,
      investor_user: this.userData.auth_details.id,
      bid: bidRequest
    });

    this.auctionApi.createBidThread(bidThreadRequest).subscribe((bidResp: BidNegotiation) => {
      if (bidResp.id) {
        this.toastr.success(INVESTOR_MESSAGES.INVESTOR_SUBMITS_BID);
      } else {
        this.toastr.warning(INVESTOR_MESSAGES.INVESTOR_SUBMIT_BID_FAILED);
      }
      this.closeSidePanel(bidResp);
    });
  }

  placeBidOnExistingThread(bidRequest) {
    this.auctionApi.submitBid(bidRequest).subscribe((bidResp: BidNegotiation) => {
      if (bidResp.id) {
        this.toastr.success(
          bidRequest.state_value === BID_ACTIONS.PLACE_BID ?
            INVESTOR_MESSAGES.INVESTOR_SUBMITS_BID :
            ISSUER_MESSAGES.COUNTER_OFFER_SUCCESS
        );
      } else {
        this.toastr.warning(ISSUER_MESSAGES.ISSUER_SUBMIT_BID_FAILED);
      }
      this.closeSidePanel(bidResp);
    });
  }

}
